import { useEffect, useState, useCallback, createContext, useContext, useRef } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '../../utils/cn'

type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description?: string
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (opts: Omit<Toast, 'id'>) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  warning: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

const variantStyles: Record<ToastVariant, { icon: typeof CheckCircle2; containerClass: string; iconClass: string }> = {
  success: {
    icon: CheckCircle2,
    containerClass: 'bg-ok/10 border-ok/30',
    iconClass: 'text-ok',
  },
  error: {
    icon: XCircle,
    containerClass: 'bg-bad/10 border-bad/30',
    iconClass: 'text-bad',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'bg-warn/10 border-warn/30',
    iconClass: 'text-warn',
  },
  info: {
    icon: Info,
    containerClass: 'bg-info/10 border-info/30',
    iconClass: 'text-info',
  },
}

const DEFAULT_DURATION = 4000
const ACTION_DURATION = 8000
const MAX_TOASTS = 5

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [exiting, setExiting] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const style = variantStyles[toast.variant]
  const Icon = style.icon

  const startExit = useCallback(() => {
    setExiting(true)
    setTimeout(() => onDismiss(toast.id), 200)
  }, [onDismiss, toast.id])

  useEffect(() => {
    const dur = toast.duration ?? (toast.action ? ACTION_DURATION : DEFAULT_DURATION)
    if (dur > 0) {
      timerRef.current = setTimeout(startExit, dur)
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [toast.duration, toast.action, startExit])

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        'pointer-events-auto flex items-start gap-3 w-full max-w-sm border rounded-xl px-4 py-3 shadow-lg transition-all duration-200',
        style.containerClass,
        exiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0',
      )}
    >
      <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', style.iconClass)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primary">{toast.title}</p>
        {toast.description && (
          <p className="text-xs text-secondary mt-0.5">{toast.description}</p>
        )}
        {toast.action && (
          <button
            onClick={() => { toast.action!.onClick(); startExit() }}
            className="text-xs font-medium text-brand hover:underline mt-1"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={() => startExit()}
        className="flex-shrink-0 p-1 rounded-lg hover:bg-overlay/10 min-w-[28px] min-h-[28px] flex items-center justify-center"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4 text-muted" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const addToast = useCallback((opts: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setToasts(prev => [...prev.slice(-(MAX_TOASTS - 1)), { ...opts, id }])
  }, [])

  const success = useCallback((title: string, description?: string) => {
    addToast({ variant: 'success', title, description })
  }, [addToast])

  const error = useCallback((title: string, description?: string) => {
    addToast({ variant: 'error', title, description, duration: 6000 })
  }, [addToast])

  const warning = useCallback((title: string, description?: string) => {
    addToast({ variant: 'warning', title, description, duration: 5000 })
  }, [addToast])

  const info = useCallback((title: string, description?: string) => {
    addToast({ variant: 'info', title, description })
  }, [addToast])

  const ctx: ToastContextValue = { toast: addToast, success, error, warning, info, dismiss }

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div
        aria-label="Notifications"
        className="fixed bottom-20 sm:bottom-6 right-4 left-4 sm:left-auto z-[100] flex flex-col items-end gap-2 pointer-events-none"
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
